/**
 * @param {object} context
 * @param {boolean} [context.hasRows]
 */
export function buildQmsDocumentLibraryTourSteps(context = {}) {
  const { hasRows = false } = context;

  const steps = [
    {
      id: "welcome",
      placement: "center",
      title: "Welcome to Document Library",
      body: "This walkthrough explains how published documents appear here and how to use the table — add or remove columns, freeze and reorder them, filter rows, preview files, and export. Reopen anytime from Guide.",
    },
    {
      id: "guide-button",
      target: '[data-tour="qms-doc-lib-guide"]',
      placement: "bottom",
      title: "Page guide",
      body: "Use Guide to replay this tutorial whenever you need a refresher on Document Library features.",
    },
    {
      id: "publish-flow",
      placement: "center",
      title: "How documents get here",
      body: "Documents are created in My Vault, reviewed, then published. Publish creates the version, review schedule, mandatory reading, and quiz flags. The library list loads from the server after publish. Open a document to manage staff and other actions from the side menu.",
    },
    {
      id: "table",
      target: '[data-tour="qms-doc-lib-table"]',
      placement: "top",
      title: "The document table",
      body: "Each row is one published document. The table uses server pagination — only the current page is loaded. Use the footer to change rows per page and move between pages.",
    },
    {
      id: "table-header",
      target: '[data-tour="qms-doc-lib-columns"]',
      placement: "bottom",
      title: "Column headers",
      body: "Every column has a title row and a filter row underneath. Icons on the right let you add columns, freeze, or remove optional columns. The grip handle (⋮⋮) lets you drag non-frozen columns to reorder them.",
    },
    {
      id: "add-column",
      target: '[data-tour="qms-doc-lib-add-column"]',
      placement: "bottom",
      title: "Add a column",
      body: "Click + on any column header to open the column picker. Choose Type, Category, File Size, Owner, or Created Date to insert that column after the current one. Default columns (ID, Document, Mandatory Reading, and so on) are always shown.",
    },
    {
      id: "remove-column",
      placement: "center",
      title: "Remove a column",
      body: "After you add an optional column with +, a red × appears on that header. Click × to hide it again. Default columns such as ID and Document cannot be removed.",
    },
    {
      id: "freeze-column",
      target: '[data-tour="qms-doc-lib-freeze-column"]',
      placement: "bottom",
      title: "Freeze a column",
      body: "Click the lock icon to freeze a column so it stays visible while you scroll horizontally. The ID column is frozen by default. Click the unlock icon to unfreeze — the first column (ID) always stays frozen.",
    },
    {
      id: "reorder-column",
      target: '[data-tour="qms-doc-lib-drag-column"]',
      placement: "bottom",
      title: "Reorder columns",
      body: "Drag a column header by the ⋮⋮ handle to move it left or right. Frozen columns cannot be dragged.",
    },
    {
      id: "filter-document",
      target: '[data-tour="qms-doc-lib-filter-document"]',
      placement: "bottom",
      title: "Text search filters",
      body: "Type in a column search box to filter on the server. Document name, Owner, Published By, Mandatory Reading, and Quiz Applied support text search. Results update automatically after a short pause.",
    },
    {
      id: "filter-id",
      target: '[data-tour="qms-doc-lib-filter-id"]',
      placement: "bottom",
      title: "ID number search",
      body: "In the ID column, enter numbers only (for example 1234) to find documents by their unique number or version. Full IDs look like SOP_CLI_01234_V1.",
    },
    {
      id: "filter-dropdown",
      placement: "center",
      title: "Dropdown filters",
      body: "When Type or Category columns are visible, use All types or All categories dropdowns to narrow the list. These filters run on the server together with your other active filters.",
    },
    {
      id: "filter-date",
      target: '[data-tour="qms-doc-lib-filter-review-date"]',
      placement: "bottom",
      title: "Date range filters",
      body: "Review Date filters by due date range. Created Date and Published Date use the same picker when those columns are visible. Choose a preset or Custom dates; All Time clears the filter.",
    },
    {
      id: "review-status-column",
      target: '[data-tour="qms-doc-lib-review-status-col"]',
      placement: "bottom",
      title: "Review Status column",
      body: "Overdue and due-soon information appears here (for example 88 day(s) overdue or Due in 5 day(s)). Review Date stays on one line with duration and due date only, so row height stays even.",
    },
    {
      id: "saved-layout",
      target: '[data-tour="qms-doc-lib-reset-layout"]',
      placement: "top",
      title: "Saved table layout",
      body: "Your column order, which optional columns are visible, and freeze choices are saved automatically in this browser. Reset layout restores the default setup if you want to start over.",
    },
    {
      id: "preview",
      target: '[data-tour="qms-doc-lib-doc-link"]',
      placement: "bottom",
      title: "Open a document",
      body: "Click a document name to open the workspace. Use Guide inside the modal to learn Preview or Document Library Staff for the section you have selected.",
      when: () => hasRows,
    },
    {
      id: "footer",
      target: '[data-tour="qms-doc-lib-footer"]',
      placement: "top",
      title: "Footer controls",
      body: "See how many entries match your filters, export to Excel, reset the table layout, change rows per page, and move between pages.",
    },
    {
      id: "export",
      target: '[data-tour="qms-doc-lib-export"]',
      placement: "top",
      title: "Export to Excel",
      body: "Download all rows matching your current filters (up to 10,000) using the columns currently visible in the table.",
    },
    {
      id: "done",
      placement: "center",
      title: "You are ready",
      body: "Use + and × to shape the table, lock and drag columns as needed, filter from the header row, open documents from the name link, and export when you need a spreadsheet. Use Refresh in Actions to reload the latest data.",
    },
  ];

  return steps.filter((step) => (step.when ? step.when(context) : true));
}
